import {
  ArrowLeft,
  BookOpen,
  Clock,
  Eye,
  FileText,
  Film,
  Gamepad2,
  Heart,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Footer from "../../components/layout/Footer";
import Button from "../../components/ui/Button";
import Header from "../../components/ui/Header";
import Input from "../../components/ui/Input";
import useSearch from "../../hooks/useSearch";
import SearchDashboard from "./components/SearchDashboard";
import SearchResults from "./components/SearchResults";

const SearchPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [isFilterOpen, setIsFilterOpen] = useState(false); // State for filter dropdown
  const [isSortOpen, setIsSortOpen] = useState(false); // State for sort dropdown

  const filterRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close filter dropdown if clicked outside
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }

      // Close sort dropdown if clicked outside
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const {
    query,
    setQuery,
    filter,
    setFilter,
    sort,
    setSort,
    isLoading,
    results,
    resetSearch,
    fetchNextPage,
    isTyping,
    hasNextPage,
  } = useSearch({ initialQuery });

  const menuType = [
    {
      key: "movie",
      value: t("search.movie"),
      icon: <Film className="h-4 w-4" />,
    },
    {
      key: "game",
      value: t("search.game"),
      icon: <Gamepad2 className="h-4 w-4" />,
    },
    {
      key: "comic",
      value: t("search.chapter"),
      icon: <BookOpen className="h-4 w-4" />,
    },
    {
      key: "novel",
      value: t("search.novel"),
      icon: <FileText className="h-4 w-4" />,
    },
  ];

  // Get active filter name for display
  const getActiveFilterName = () => {
    const activeFilter = menuType.find((item) => item.key === filter);
    return activeFilter ? activeFilter.value : t("search.type");
  };

  // Get active sort name for display
  const getActiveSortName = () => {
    const activeSort = menuSort.find((item) => item.key === sort);
    return activeSort ? activeSort.value : t("search.sort");
  };

  const menuSort = [
    {
      key: "views__desc",
      value: t("search.viewer"),
      icon: <Eye className="h-4 w-4" />,
    },
    {
      key: "like_count__desc",
      value: t("search.likes"),
      icon: <Heart className="h-4 w-4" />,
    },
    {
      key: "created_at__desc",
      value: t("search.newest"),
      icon: <Clock className="h-4 w-4" />,
    },
    {
      key: "views_day__desc",
      value: t("search.viewDay"),
      icon: <Eye className="h-4 w-4" />,
    },
    {
      key: "views_week__desc",
      value: t("search.viewWeek"),
      icon: <Eye className="h-4 w-4" />,
    },
    {
      key: "rating_total__desc",
      value: t("search.viewRating"),
      icon: <Heart className="h-4 w-4" />,
    },
  ];

  const toggleFilterDropdown = () => setIsFilterOpen((prev) => !prev);
  const toggleSortDropdown = () => setIsSortOpen((prev) => !prev);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams({ q: query });
  };

  const handleFilterChange = (filterKey: string) => {
    setFilter(filterKey);
    setSearchParams({ q: query, filter: filterKey });
  };

  const handleSortChange = (sortKey: string) => {
    setSort(sortKey);
    setSearchParams({ q: query, filter, sort: sortKey });
  };

  console.log(query && !filter && !sort, "filter", filter, "sort", sort);

  return (
    <div className="bg-mainBlack min-h-screen w-full">
      <Header
        renderBottomNav={
          <div className="sticky top-0 z-50">
            <div className="mx-auto px-4 h-16 flex items-center gap-3">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <Search className="h-4 w-4" />
                  </span>
                  <Input
                    type="text"
                    placeholder={t("search.search")}
                    value={query}
                    onChange={handleSearchInputChange}
                    className="w-full pl-10 bg-gray-900 border-gray-700 text-white"
                    autoFocus
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={resetSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="flex flex-wrap gap-4 px-4 py-2">
              {/* Filter Dropdown */}
              <div className="relative" ref={filterRef}>
                <Button
                  variant="outline"
                  size="icon"
                  className={`relative flex items-center gap-2 ${
                    filter ? "bg-gray-800 border-blue-500" : ""
                  }`}
                  onClick={toggleFilterDropdown}
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  <span className="text-sm">{getActiveFilterName()}</span>
                  {filter && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
                  )}
                </Button>
                {isFilterOpen && (
                  <div className="absolute left-0 mt-2 w-56 bg-gray-900 border-gray-700 text-white rounded shadow-lg z-50">
                    {menuType.map((type) => (
                      <div
                        key={type.key}
                        className={`px-4 py-2 hover:bg-gray-800 cursor-pointer flex items-center gap-2 ${
                          filter === type.key ? "bg-gray-800" : ""
                        }`}
                        onClick={() => {
                          handleFilterChange(type.key);
                          setIsFilterOpen(false);
                        }}
                      >
                        {type.icon}
                        {type.value}
                        {filter === type.key && (
                          <span className="ml-auto">✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sort Dropdown */}
              <div className="relative" ref={sortRef}>
                <Button
                  variant="outline"
                  size="icon"
                  className={`relative flex items-center gap-2 ${
                    sort ? "bg-gray-800 border-blue-500" : ""
                  }`}
                  onClick={toggleSortDropdown}
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  <span className="text-sm">{getActiveSortName()}</span>
                  {sort && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
                  )}
                </Button>
                {isSortOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-gray-900 border-gray-700 text-white rounded shadow-lg z-50">
                    {menuSort.map((sortOption) => (
                      <div
                        key={sortOption.key}
                        className={`px-4 py-2 hover:bg-gray-800 cursor-pointer flex items-center gap-2 ${
                          sort === sortOption.key ? "bg-gray-800" : ""
                        }`}
                        onClick={() => {
                          handleSortChange(sortOption.key);
                          setIsSortOpen(false);
                        }}
                      >
                        {sortOption.icon}
                        {sortOption.value}
                        {sort === sortOption.key && (
                          <span className="ml-auto">✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        }
      />

      {!query && !filter && !sort ? (
        <SearchDashboard />
      ) : (
        <SearchResults
          results={results}
          isLoading={isLoading}
          filters={menuType.map((type) => type.key)}
          selectedFilter={filter}
          onFilterChange={setFilter}
          loadMore={fetchNextPage}
          isTyping={isTyping} // Pass typing state from useSearch hook
          //
          hasNextPage={hasNextPage} // Check if there's a next page
          isFetchingNextPage={isLoading} // Show loading spinner when fetching next page
          fetchNextPage={fetchNextPage} // Function to fetch next page
        />
      )}
      <Footer />
    </div>
  );
};

export default SearchPage;
